import React, { ReactNode } from 'react';
import {
  Badge, Box, Flex, Text, useColorModeValue,
} from '@chakra-ui/react';
import DashPanel from './DashPanel';

// "What you're working on" (item 96) — the active-work region's primary list: the
// concrete next actions (proposals awaiting a decision, balances left to bill, open
// work orders). Built client-side in useDashboardMetrics; each row deep-links.
export interface WorkingOnItem {
  id: string;
  icon: ReactNode;
  label: string;
  sub: string;
  note: string;
  accent: string;
  href: string;
}

const WorkingOnCard: React.FC<{ items: WorkingOnItem[], onOpen?: (href: string) => void }> = ({ items, onOpen }) => {
  const labelColor = useColorModeValue('gray.800', 'whiteAlpha.900');
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const chipBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const rowHover = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');

  return (
    <DashPanel label="What you're working on" accent="brandGreen.400">
      {items.length === 0 && (
        <Text fontSize="sm" color={subColor}>You&apos;re all caught up — nothing needs action.</Text>
      )}
      <Flex direction="column">
        {items.map((it) => (
          <Flex
            key={it.id}
            align="center"
            gap={3}
            py={2.5}
            px={2}
            mx={-2}
            borderRadius="lg"
            cursor="pointer"
            transition="background 0.15s"
            _hover={{ bg: rowHover }}
            onClick={() => (onOpen ? onOpen(it.href) : undefined)}
          >
            <Flex
              w="32px"
              h="32px"
              flexShrink={0}
              align="center"
              justify="center"
              borderRadius="lg"
              bg={chipBg}
              color={it.accent}
              fontSize="sm"
            >
              {it.icon}
            </Flex>
            <Box minW={0} flex={1}>
              <Text fontSize="sm" fontWeight="semibold" color={labelColor} noOfLines={1}>{it.label}</Text>
              <Text fontSize="xs" color={subColor} noOfLines={1}>{it.sub}</Text>
            </Box>
            <Badge
              flexShrink={0}
              fontSize="0.6rem"
              colorScheme={it.accent.startsWith('brandGreen') ? 'green' : 'blue'}
              variant="subtle"
              textTransform="none"
            >
              {it.note}
            </Badge>
          </Flex>
        ))}
      </Flex>
    </DashPanel>
  );
};

WorkingOnCard.defaultProps = { onOpen: undefined };

export default WorkingOnCard;
